import React, { createContext, useMemo, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useQueryClient } from '@tanstack/react-query';

import { useAuth } from '../../authentication';
import {
  useGetUsersQuery,
  useGetInvitationsQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useAcceptInvitationMutation,
  useDeclineInvitationMutation,
  useUpdateUserEntitlementMutation
} from '../hooks';
import { useOrganizations } from './OrganizationsProvider';
import { useNotifications } from '../../../providers/NotificationsProvider';
import { useRoles } from './RolesProvider';

const UsersContext = createContext();

const UsersProvider = ({ children = null }) => {
  const { accessToken, user } = useAuth();
  const { currentOrganization } = useOrganizations();
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();
  const { roles } = useRoles();

  const usersQuery = useGetUsersQuery(accessToken, currentOrganization?.id);
  const invitationsQuery = useGetInvitationsQuery(accessToken);
  const acceptInvitationMutation = useAcceptInvitationMutation(accessToken);
  const declineInvitationMutation = useDeclineInvitationMutation(accessToken);
  const createUserMutation = useCreateUserMutation(accessToken);
  const updateUserMutation = useUpdateUserMutation(accessToken);
  const deleteUserMutation = useDeleteUserMutation(accessToken);
  const updateUserEntitlementMutation = useUpdateUserEntitlementMutation(accessToken);

  const queryKey = ['membership', 'users', currentOrganization?.id];

  const updateUserEntitlement = useCallback(
    async (_user, role) => {
      const prevState = await queryClient.getQueryData(queryKey);
      const updatedUser = usersQuery.data.find((u) => u.id === _user.id);
      const entitlementId = updatedUser.memberships[0].entitlements[0].id;

      try {
        queryClient.setQueryData(
          queryKey,
          prevState.map((u) => {
            if (u.id === _user.id) {
              return {
                ..._user,
                memberships: [
                  {
                    ..._user.memberships[0],
                    entitlements: [
                      {
                        ..._user.memberships[0].entitlements[0],
                        roleId: role.id,
                        role
                      }
                    ]
                  }
                ]
              };
            }

            return u;
          })
        );

        await updateUserEntitlementMutation.mutateAsync({
          organizationId: currentOrganization?.id,
          entitlementId,
          roleId: role.id
        });

        addNotification({
          type: 'info',
          message: 'User role has been updated'
        });
      } catch (error) {
        addNotification({
          type: 'error',
          message: 'Failed to update user role'
        });
        queryClient.setQueryData(queryKey, () => prevState);
      }
    },
    [updateUserEntitlementMutation, usersQuery?.data, currentOrganization?.id]
  );

  const createUser = useCallback(
    async (data) => {
      const prevState = await queryClient.getQueryData(queryKey);
      try {
        const role = roles.find((r) => r.id === data.roleId);
        queryClient.setQueryData(queryKey, (old) => [
          ...old,
          {
            email: data.email,
            memberships: [
              {
                organizationId: currentOrganization.id,
                status: 'invited',
                entitlements: [{ role }]
              }
            ]
          }
        ]);

        const newUser = await createUserMutation.mutateAsync({
          data,
          organizationId: currentOrganization.id
        });
        addNotification({
          type: 'info',
          message: `Account has been successfully created: ${data.email}`
        });

        queryClient.setQueryData(queryKey, (old) =>
          old.map((u) => (u.email === newUser.email ? newUser : u))
        );
      } catch (error) {
        queryClient.setQueryData(queryKey, () => prevState);
        addNotification({
          type: 'error',
          message: 'Failed to create user'
        });
      }
    },
    [currentOrganization?.id, createUserMutation, usersQuery.data]
  );

  const updateUser = useCallback(
    async (userId, data) => {
      const prevState = await queryClient.getQueryData(queryKey);
      try {
        queryClient.setQueryData(queryKey, (old) =>
          old.map((_user) => (_user.id === userId ? { ..._user, ...data } : _user))
        );
        await updateUserMutation.mutateAsync({
          id: userId,
          data
        });
      } catch (error) {
        queryClient.setQueryData(queryKey, () => prevState);
      }
    },
    [updateUserMutation, usersQuery.data]
  );

  const deleteUser = useCallback(
    async (_user) => {
      const prevState = await queryClient.getQueryData(queryKey);
      try {
        queryClient.setQueryData(queryKey, (old) => old?.filter((i) => i.id !== _user.id));
        await deleteUserMutation.mutateAsync({
          userId: _user.id,
          organizationId: currentOrganization.id
        });

        addNotification({
          type: 'info',
          message: `${_user.email} has been deleted`
        });

        usersQuery.refetch();
      } catch (error) {
        addNotification({
          type: 'error',
          message: `Failed to delete ${_user.email}`
        });
        queryClient.setQueryData(queryKey, () => prevState);
      }
    },
    [currentOrganization?.id, deleteUserMutation, usersQuery.data]
  );

  const acceptInvitation = useCallback(
    async (invitation) => {
      queryClient.setQueryData(['membership', 'organizations'], (old) => [
        ...old,
        invitation.organization
      ]);

      queryClient.setQueryData(['membership', 'invitations'], (old) => [
        ...old.map((oldUser) => {
          const updatedUser = { ...oldUser };
          updatedUser.memberships = updatedUser.memberships.map((membership) => {
            if (membership.id === invitation.id) {
              return { ...membership, status: 'active' };
            }
            return membership;
          });

          return updatedUser;
        })
      ]);

      acceptInvitationMutation.mutateAsync(invitation.id);
    },
    [acceptInvitationMutation, invitationsQuery]
  );

  const declineInvitation = useCallback(
    async (invitation) => {
      await declineInvitationMutation.mutateAsync(invitation.id);

      queryClient.setQueryData(['membership', 'invitations'], (old) => [
        ...old.map((oldUser) => {
          const newUser = { ...oldUser };
          newUser.memberships = newUser.memberships.filter(
            (membership) => membership.id !== invitation.id
          );
          return newUser;
        })
      ]);
    },
    [acceptInvitationMutation, usersQuery.data]
  );

  const activeUsers = useMemo(
    () =>
      usersQuery.data?.filter(
        (item) =>
          item.auth0Id !== user?.sub &&
          item.memberships.find(
            (membership) => membership.organizationId === currentOrganization?.id
          ).status === 'active'
      ),
    [usersQuery.data, currentOrganization?.id]
  );

  const currentUser = useMemo(
    () => usersQuery.data?.find((u) => u.auth0Id === user?.sub),
    [usersQuery.data]
  );

  const invitations = useMemo(
    () =>
      invitationsQuery.data?.length > 0
        ? invitationsQuery.data[0].memberships.filter(({ status }) => status === 'invited')
        : [],
    [invitationsQuery.data]
  );

  const value = useMemo(
    () => ({
      users: usersQuery.data,
      activeUsers,
      invitations,
      createUser,
      updateUser,
      deleteUser,
      acceptInvitation,
      declineInvitation,
      updateUserEntitlement,
      currentUser
    }),
    [usersQuery.data, invitationsQuery.data, createUser, updateUser, deleteUser, acceptInvitation]
  );

  if (usersQuery.isInitialLoading) return false;
  if (invitationsQuery.isInitialLoading) return false;

  return <UsersContext.Provider value={value}>{children}</UsersContext.Provider>;
};

UsersProvider.propTypes = {
  children: PropTypes.node
};

const useUsers = () => useContext(UsersContext);

export { UsersContext, UsersProvider, useUsers };
